// `on-click-outside` / `click-outside-active` / `click-outside-additional`, as a hook.
//
// ⚠️ EVERY MENU IN THIS PORT STAYED OPEN UNTIL ITS OWN TRIGGER WAS CLICKED AGAIN, because the
// two mechanisms upstream relies on are both absent here. `uib-dropdown` registers with
// `uibDropdownService`, which closes the open dropdown on any document click; the tooltip
// menus declare `on-click-outside` explicitly. Neither survives a port to React, and nothing
// replaced them — so opening the alert list and then clicking a candidate left the list
// hanging over the row that was just clicked.
//
// ⚠️ IT MATCHES BY SELECTOR, NOT BY REF, and that is deliberate. A ref needs an element to
// hang off, and the elements here are deep inside ported markup whose class names and box
// structure are a pixel contract (CLAUDE.md 4) — wrapping a menu in a div to get a ref is
// exactly the kind of "harmless" extra box that moves a centred layout by a fraction and
// costs a sweep to find. Upstream's own `click-outside-additional="['.tooltip']"` is a
// selector list too, so this is the shape it already had.
//
// ⚠️ AND IT LISTENS ON `mousedown`, NOT `click`. A `click` fires after `mouseup`, by which
// time a re-render triggered by the same gesture may have replaced the element the event
// started on — the target then matches nothing, the menu closes, and the click that was meant
// to land inside it never reaches its handler.
import { useEffect, useRef } from 'react'

export interface DismissOnOutsideClickOptions {
  /** `click-outside-active` — nothing is listening while the menu is shut. */
  open: boolean
  /** `on-click-outside`. */
  onDismiss: () => void
  /**
   * Selectors a click may land inside without closing: the menu, its trigger, and anything
   * upstream lists in `click-outside-additional`.
   *
   * ⚠️ THE TRIGGER BELONGS IN HERE. Leaving it out makes the second click on a toggle close
   * the menu from this hook AND re-open it from the trigger's own handler — or the reverse,
   * depending on order — so the menu cannot be shut with the control that opened it.
   */
  within: readonly string[]
}

export function useDismissOnOutsideClick({ open, onDismiss, within }: DismissOnOutsideClickOptions): void {
  // Joined so the effect re-runs when the LIST changes rather than when the array identity
  // does — callers build it inline, which is a new array on every render.
  const selector = within.join(',')

  // ⚠️ THE CALLBACK IS HELD IN A REF SO IT IS NOT A DEPENDENCY. Every call site writes
  // `onDismiss={() => setOpen(false)}`, which is a new function on every render; as a
  // dependency that detaches and re-attaches a document listener on each one. Keeping it here
  // means the effect runs when the menu OPENS and when it CLOSES, which is what it is about.
  const latest = useRef(onDismiss)
  latest.current = onDismiss

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target
      // `closest` is on Element; a click can land on the document or on a text node's parent
      // being removed, and neither is "inside".
      if (target instanceof Element && selector.length > 0 && target.closest(selector) !== null) return
      latest.current()
    }

    document.addEventListener('mousedown', onPointerDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true)
    }
  }, [open, selector])
}
